<template>
	<div ref="container" class="flow-highlight">
		<div v-if="showSearch" class="f-h-search-panel">
			<SearchInput
				ref="highlight-search"
				placeholder="Search"
				:value.sync="searchKeyword"
				:no-result-tag="showNoResult"
				@keyup="highlightKeyword($event)"
			/>
		</div>
		<pre v-highlightjs="content" :wrap="wrap">
		<code ref="code" class="flow-highlight-code" :class="[highlightClass]" :wrap="wrap" />
		</pre>
	</div>
</template>
<script>
import hljs from "highlight.js";
import Vue, { defineComponent } from "vue";
import SearchInput from "./../SearchInput/SearchInput";
import "highlight.js/styles/atelier-forest-dark.css";

export default defineComponent({
	name: "Highlight",
	components: {
		SearchInput
	},
	props: {
		/**
		 * Content to highlight
		 */
		content: {
			type: String,
			default: null
		},
		/**
		 * Auto scroll when content is updated
		 */
		autoScroll: {
			type: Boolean,
			default: false
		},
		/**
		 * true | false
		 */
		wrap: {
			type: Boolean,
			default: true
		},
		/**
		 * true | false
		 */
		showSearch: {
			type: Boolean,
			default: false
		},
		/**
		 * Specify class for highlight.js <br/> this class is related to type content which you want to format <br/> E.g. yml, css, html.
		 */
		highlightClass: {
			type: String,
			default: ""
		}
	},
	data() {
		return {
			searchKeyword: null,
			noResults: false,
			enterPress: 0
		};
	},
	computed: {
		showNoResult: {
			get() {
				return this.searchKeyword !== null && this.searchKeyword !== "" && this.noResults;
			}
		}
	},
	watch: {
		content: {
			handler() {
				if (this.autoScroll) {
					this.$nextTick(() => {
						this.$refs.container.scrollTop = this.$refs.container.scrollHeight;
					});
				}
			}
		},
		showSearch: {
			handler() {
				if (this.showSearch) {
					this.$nextTick(() => {
						//console.log(this.$refs["highlight-search"]);
						this.$refs["highlight-search"].$refs["search-input"].focus();
					});
				}
			}
		}
	},
	beforeMount() {
		const addLineNumbers = () => {
			const code = this.$el.querySelector("pre code.flow-highlight-code");
			code.innerHTML = code.innerHTML
				.split("\n")
				.map((line, index) => `<span class="line-number">${index + 1}</span> ${line}`)
				.join("\n");
		};
		Vue.directive("highlightjs", {
			deep: true,
			bind(el, binding) {
				// on first bind, highlight all targets
				const targets = el.querySelectorAll("code");
				targets.forEach(target => {
					// if a value is directly assigned to the directive, use this
					// instead of the element content.
					if (binding.value) {
						target.textContent = binding.value;
					}
					hljs.highlightBlock(target);
				});
			},
			inserted(el) {
				addLineNumbers(el);
			},

			componentUpdated(el, binding) {
				// after an update, re-fill the content and then highlight
				const targets = el.querySelectorAll("code");
				targets.forEach(target => {
					if (binding.value) {
						target.textContent = binding.value;
						hljs.highlightBlock(target);
					}
				});
				addLineNumbers(el);
			}
		});
	},
	methods: {
		highlightKeyword(e) {
			//console.log(e);
			if (e.keyCode === 13) {
				this.enterPress += 1;

				const scrollElementLists = this.$el.querySelectorAll(
					"pre code.flow-highlight-code span.highlight"
				);
				const scrollHere = scrollElementLists[this.enterPress];

				//	console.log("onEnter : ", this.enterPress, scrollHere);
				if (scrollHere) {
					scrollHere.scrollIntoView({ behavior: "smooth", block: "center" });
				}
			} else {
				this.enterPress = 0;
				const keyword = this.searchKeyword;
				if (keyword !== null && keyword !== "") {
					const iQuery = new RegExp(`(${keyword})(?![^<]*>|[^<>]${keyword}</)`, "ig");
					const code = this.$el.querySelector("pre code.flow-highlight-code");
					code.innerHTML = code.innerHTML.replace(iQuery, function (matchedTxt) {
						return `<span class='highlight'>${matchedTxt}</span>`;
					});
				}
				const scrollHere = this.$el.querySelector("pre code.flow-highlight-code span.highlight");
				if (scrollHere) {
					this.noResults = false;
					scrollHere.scrollIntoView({ behavior: "smooth", block: "center" });
				} else {
					this.noResults = true;
				}
			}
		}
	}
});
</script>
<style lang="scss">
$background: var(--dashboard-background);
div.flow-highlight {
	// overflow: auto;
	height: 100%;

	.hljs {
		background: $background;

		transition: height 1s;
		padding: 0px;
		&[wrap] {
			padding-left: 50px;
			white-space: pre-wrap;
			word-break: break-word;
		}
	}

	.hljs,
	.hljs-subst {
		color: var(--dashboard-font);
	}

	pre {
		background-color: $background;
		padding: 12px 12px 12px 0px;
		display: flex;

		&[wrap] {
			white-space: inherit;
		}
		code,
		.line-number {
			font-family: var(--log-font);
		}

		code.flow-highlight-code {
			position: relative;
			&[wrap] {
				&:before {
					content: "";
					position: absolute;
					left: 0;
					width: 40px;
					// border-right: 1px solid var(--dashboard-font-light);
					bottom: 0px;
					top: 0px;
				}

				.line-number {
					margin-left: -60px;
					position: relative;
					border-right: none;
					left: unset;
				}
			}

			.highlight {
				background-color: orange;
				color: white;
			}

			.line-number {
				width: 36px;
				display: inline-block;
				color: var(--dashboard-input-placeholder);
				text-align: center;
				font-size: 10px;

				position: sticky;
				left: -10px;
				margin-left: -10px;
				background-color: $background;

				// border-right: 1px solid var(--dashboard-font-light);
			}
		}
	}

	> div.flow-highlight-toolbar {
		padding: 16px;
		display: flex;
		flex-direction: row-reverse;

		> * {
			margin-right: 12px;
		}
	}
	> div.f-h-search-panel {
		padding: 16px 16px 0px 16px;
		position: -webkit-sticky;
		position: sticky;
		top: 0;
		z-index: 1;
		background: $background;
	}
}
</style>
